import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ActualizarMultiplesPreferencias, ActualizarPreferencia, ObtenerPreferencias } from "../services/ApiCasos/Alertas.service";
import { QueryKey } from "../types/enums/QueryKey.enum";
import type { ApiError, Source, SourceList } from "../types/interfaces/Source";
import type { ActualizarMultiplesPreferenciasRequest, ActualizarMultiplesPreferenciasResponse, ActualizarPreferenciaRequest, ActualizarPreferenciaResponse, ObtenerPreferenciasRequest, ObtenerPreferenciasResponse } from "../types/interfaces/ApiCasos/Alertas.interface";
import type { TipoUsuarioPerfilOpcionEnum } from "@/modules/escritorio/types/enums/TipoUsuario.enum";
import { TipoUsuarioConsulta, useTipoUsuarioPerfilConsulta } from "./useTipoUsuarioPerfilConsulta.hook";
import { AboutMe, BackUpCodesTwoFA, DisableTwoFA, EnableTwoFA, QRImageTwoFA, VerifyTwoFA } from "../services/GestionCasosApi/Autenticacion.service";
import type { AboutMeResponse, BackUpCodesTwoFAResponse, DisableTwoFAResponse, EnableTwoFAResponse } from "../types/interfaces/GestionCasosApi/Authentication.interface";
import type { Verify2FARequest } from "@/modules/login/types/interfaces/AutenticacionV2";
import { useRevokeAllSessions } from "./useChangePassword.hook";
import { storage } from "../utils/Const.utils";
import { StorageKeys } from "../types/enums/Storage.enum";
import type { AuthUser } from "../contexts/AuthContext";
import type { GuardarDatosUsuariosRequest } from "../types/interfaces/ApiGestionPartes/Contactos.interface";
import { GuardarDatosUsuarios, GuardarDatosUsuariosV2 } from "../services/ApiGestionPartes/Contactos.service";


export enum Destinatario {
    Personal = 1,
    Entidad = 3,
    ND = 90,
}

export const useIdDestinatarioByUserLogged = (tipoUsuario: TipoUsuarioPerfilOpcionEnum) => {
    
    let { tipoUsuarioConsulta } = useTipoUsuarioPerfilConsulta(tipoUsuario);
    
    let idDestinatario : Destinatario;
    
    if(tipoUsuarioConsulta === TipoUsuarioConsulta.PERSONA_FISICA) return idDestinatario = Destinatario.Personal 
    if(tipoUsuarioConsulta === TipoUsuarioConsulta.ABOGADO_PARA_LEGAL || tipoUsuarioConsulta === TipoUsuarioConsulta.ADMIN_MINISTERIO_PUBLICO) return idDestinatario = Destinatario.Entidad
    
    return idDestinatario = Destinatario.ND 
}

export const useObtenerPreferencias = (data: ObtenerPreferenciasRequest) => {
    return useQuery<SourceList<ObtenerPreferenciasResponse>>({
        queryKey: [QueryKey.ObtenerPreferencias],
        queryFn: async () => await ObtenerPreferencias(`?idUsuario=${data.idUsuario}&idDestinatario=${data.idDestinatario}`),
        refetchOnWindowFocus: false,
        staleTime: 3600000, // 60 minutos en milisegundos
        enabled: !!data.idUsuario || !!data.idDestinatario
    })
}

export const useActualizarPreferencia = () => {

    const queryClient = useQueryClient();

    return useMutation<ActualizarPreferenciaResponse, ApiError, ActualizarPreferenciaRequest>({
        mutationFn: (data: ActualizarPreferenciaRequest) => ActualizarPreferencia(data),
        onError: (err) => {
            console.log(err)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ 
                queryKey: [QueryKey.ObtenerPreferencias]
            });
        }
    })
}

export const useActualizarMultiplesPreferencias = () => {

    const queryClient = useQueryClient();

    return useMutation<Source<ActualizarMultiplesPreferenciasResponse>, ApiError, ActualizarMultiplesPreferenciasRequest>({
        mutationFn: (data: ActualizarMultiplesPreferenciasRequest) => ActualizarMultiplesPreferencias(data),
        onError: (err) => {
            console.log(err)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ 
                queryKey: [QueryKey.ObtenerPreferencias] 
            });
        }
    })
}

export const useEnableTwoFA = () => {
    
    return useMutation<EnableTwoFAResponse, ApiError, any>({
        mutationFn: (data: any) => EnableTwoFA(),
        onError: (err) => {
            console.log(err)
        },
        onSuccess: (res) => {
            console.log(res)
        }
    })
}

export const useVerifyTwoFA = () => {
    const revokeAllSessions = useRevokeAllSessions();

    return useMutation<any, ApiError, Verify2FARequest>({
        mutationFn: (data: Verify2FARequest) => VerifyTwoFA(data),
        onError: (err) => {
            console.log(err)
        },
        onSuccess: (res) => {
            console.log(res)
            revokeAllSessions.mutate({})
        }
    })
}

export const useDisableTwoFA = () => {
    return useMutation<DisableTwoFAResponse, ApiError, any>({
        mutationFn: (data: any) => DisableTwoFA(),
        onError: (err) => {
            console.log(err)
        },
        onSuccess: (res) => {
            console.log(res)
            const USER_DATA : AuthUser = storage.get(StorageKeys.USER_DATA)
            storage.set(StorageKeys.USER_DATA, {
                ...USER_DATA,
                esActivo2FA: false
            });
        }
    })
}

export const useBackUpCodesTwoFA = () => {
    return useMutation<BackUpCodesTwoFAResponse, ApiError, any>({
        mutationFn: (data: any) => BackUpCodesTwoFA(),
        onError: (err) => {
            console.log(err)
        },
        onSuccess: (res) => {
            console.log(res)
        }
    })
}

export const useQRImageTwoFA = (makeRequest: boolean) => {
    return useQuery<any>({
        queryKey: [QueryKey.TwoFAConfig, QueryKey.QRImageTwoFA],
        queryFn: async () => await QRImageTwoFA(),
        refetchOnWindowFocus: false,
        //staleTime: 3600000, // 60 minutos en milisegundos
        enabled: !!makeRequest,
    })
}

export const useGuardarDatosUsuarios = () => {

    const queryClient = useQueryClient();

    return useMutation<any, ApiError, GuardarDatosUsuariosRequest>({
        mutationFn: (data: GuardarDatosUsuariosRequest) => GuardarDatosUsuarios(data),
        onError: (err) => {
            console.log(err)
        },
        onSuccess: (res) => {
            queryClient.invalidateQueries({ 
                queryKey: [QueryKey.AboutMe] 
            });
        }
    })
}

export const useGuardarDatosUsuariosV2 = () => {

    const queryClient = useQueryClient();

    return useMutation<any, ApiError, GuardarDatosUsuariosRequest>({
        mutationFn: (data: GuardarDatosUsuariosRequest) => GuardarDatosUsuariosV2(data),
        onError: (err) => {
            console.log(err)
        },
        onSuccess: (res) => {
            queryClient.invalidateQueries({ 
                queryKey: [QueryKey.AboutMe] 
            });
        }
    })
}

export const useAboutMe = (idUsuario: number) => {
    return useQuery<AboutMeResponse>({
        queryKey: [QueryKey.AboutMe, idUsuario],
        queryFn: async () => await AboutMe(),
        refetchOnWindowFocus: false,
        staleTime: 3600000, // 60 minutos en milisegundos
        enabled: !!idUsuario
    })
}